import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import ReactFlow, { 
  MiniMap, 
  Controls, 
  Background, 
  ReactFlowProvider,
  applyNodeChanges,
  applyEdgeChanges,
  MarkerType,
  Handle,
  Position
} from 'reactflow';
import 'reactflow/dist/style.css';
import '../../App.css';
import '../ArrowStyles.css';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

// EvoML Logo Component - fetching from the official URL
const EvoMLLogo = ({ size = 24 }) => (
  <img 
    src="https://docs.evoml.ai/img/evoml-1.png"
    alt="EvoML"
    width={size}
    height={size}
    style={{ display: 'inline-block' }}
  />
);

// Artemis SVG Logo Component
const ArtemisLogo = ({ size = 24, color = '#0C1A3A' }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 1200 1200" 
    style={{ display: 'inline-block' }}
  >
    <path 
      fill={color} 
      d="M1003.6,1085.8l139.6-80.6L655.4,160.3l0.3-0.4l-49.5-86.2L563.8,153l1.1,1l-1.6-1l-492,852.2l139.6,80.6
      l154.6-272.5l242.5,140l0.7,1.1l0.7-0.4l0.7,0.4l0.7-1.1l240.8-139L1003.6,1085.8z M609.4,768l-164.3-94.9l164.5-290l163,290.7
      L609.4,768z"
    />
  </svg>
);

// Custom node component for automated steps
const AutomatedNode = ({ data, selected }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [isClicked, setIsClicked] = useState(false);
  
  // Determine which logo to show based on platform highlight
  const getLogoToShow = () => {
    if (!data.platformHighlight) return null;
    
    // If both platforms are active, prefer showing based on which filter is stronger
    if (Array.isArray(data.platformHighlight)) {
      // For combined state, show based on node's primary platform
      if (data.platformHighlight.includes('agentic')) return 'artemis';
      if (data.platformHighlight.includes('evoml')) return 'evoml';
    } else {
      // Single platform active
      if (data.platformHighlight === 'agentic') return 'artemis';
      if (data.platformHighlight === 'evoml') return 'evoml';
    }
    return null;
  };

  // Get the clicked style for the node
  const getClickedStyle = () => {
    // Apply platform-specific styles based on platformHighlight
    const baseStyle = data.style || {};
    
    // Green gradient system based on automation level
    if (Array.isArray(data.platformHighlight) && data.platformHighlight.length > 1) {
      // Both platforms - strong green
      return {
        ...baseStyle,
        background: 'linear-gradient(135deg, #2e7d32 0%, #388e3c 100%)',
        border: '3px solid #1b5e20',
        boxShadow: '0 4px 15px rgba(46, 125, 50, 0.4)',
        color: '#ffffff',
        fontWeight: 'bold'
      };
    } else if (data.platformHighlight === 'agentic') {
      // Agentic only - medium green
      return {
        ...baseStyle,
        background: 'linear-gradient(135deg, #66bb6a 0%, #81c784 100%)',
        border: '2px solid #4caf50',
        boxShadow: '0 3px 12px rgba(76, 175, 80, 0.3)',
        color: '#ffffff',
        fontWeight: '600'
      };
    } else if (data.platformHighlight === 'evoml') {
      // EvoML only - light green
      return {
        ...baseStyle,
        background: 'linear-gradient(135deg, #c8e6c9 0%, #dcedc8 100%)',
        border: '2px solid #a5d6a7',
        boxShadow: '0 2px 8px rgba(165, 214, 167, 0.3)',
        color: '#1b5e20',
        fontWeight: '600'
      };
    }
    
    return baseStyle;
  };

  return (
    <div
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
      onClick={() => setIsClicked(!isClicked)}
      style={{
        ...getClickedStyle(),
        position: 'relative',
        cursor: 'pointer',
        transform: selected ? 'scale(1.05)' : 'scale(1)',
        transition: 'all 0.3s ease',
      }}
    >
      <Handle type="target" position={Position.Top} style={{ opacity: 0 }} />
      <Handle type="target" position={Position.Left} style={{ opacity: 0 }} />
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%'
      }}>
        {(data.icon || data.evomlLogo || data.artemisLogo) && (
          <div style={{ fontSize: '24px', marginBottom: '5px' }}>
            {(() => {
              const logoToShow = getLogoToShow();
              if (logoToShow === 'artemis') {
                return <ArtemisLogo size={24} color={data.platformHighlight ? '#ffffff' : '#333333'} />;
              } else if (logoToShow === 'evoml') {
                return <EvoMLLogo size={24} />;
              } else {
                return data.icon;
              }
            })()}
          </div>
        )}
        <div style={{ textAlign: 'center', lineHeight: 1.2 }}>{data.label}</div>
        {data.automated && (
          <div style={{
            position: 'absolute',
            top: '-10px',
            right: '-10px',
            background: '#4caf50',
            color: 'white',
            borderRadius: '50%',
            width: '24px',
            height: '24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '14px',
            fontWeight: 'bold',
            boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
          }}>
            ✓
          </div>
        )}
      </div>
      <Handle type="source" position={Position.Bottom} style={{ opacity: 0 }} />
      <Handle type="source" position={Position.Right} style={{ opacity: 0 }} />
      
      {showTooltip && data.tooltip && (
        <div style={{
          position: 'absolute',
          bottom: '100%',
          left: '50%',
          transform: 'translateX(-50%)',
          background: 'rgba(0, 0, 0, 0.9)',
          color: 'white',
          padding: '8px 12px',
          borderRadius: '6px',
          fontSize: '12px',
          whiteSpace: 'nowrap',
          marginBottom: '5px',
          zIndex: 1000,
          boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
        }}>
          {data.tooltip}
          <div style={{
            position: 'absolute',
            top: '100%',
            left: '50%',
            transform: 'translateX(-50%)',
            width: 0,
            height: 0,
            borderLeft: '5px solid transparent',
            borderRight: '5px solid transparent',
            borderTop: '5px solid rgba(0, 0, 0, 0.9)',
          }} />
        </div>
      )}
    </div>
  );
};

const nodeTypes = {
  automated: AutomatedNode,
};

const themeStyles = {
  mainStageNode: {
    background: 'linear-gradient(135deg, #ffffff 0%, #f8f8f8 100%)',
    border: '2px solid #333333',
    borderRadius: '12px',
    padding: '15px 20px',
    fontSize: '14px',
    fontWeight: '600',
    color: '#333333',
    width: 180,
    textAlign: 'center',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  },
  automatedEvoMLNode: {
    background: 'linear-gradient(135deg, #ffffff 0%, #f8f8f8 100%)',
    border: '2px solid #333333',
    borderRadius: '12px',
    padding: '15px 20px',
    fontSize: '14px',
    fontWeight: '600',
    color: '#333333',
    width: 180,
    textAlign: 'center',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  },
  automatedArtemisNode: {
    background: 'linear-gradient(135deg, #ffffff 0%, #f8f8f8 100%)',
    border: '2px solid #333333',
    borderRadius: '12px',
    padding: '15px 20px',
    fontSize: '14px',
    fontWeight: '600',
    color: '#333333',
    width: 180,
    textAlign: 'center',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  },
  bothPlatformsNode: {
    background: 'linear-gradient(135deg, #ffffff 0%, #f8f8f8 100%)',
    border: '2px solid #333333',
    borderRadius: '12px',
    padding: '15px 20px',
    fontSize: '14px',
    fontWeight: '600',
    color: '#333333',
    width: 180,
    textAlign: 'center',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  },
  timeNode: {
    background: 'linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)',
    border: '2px solid #ff9800',
    borderRadius: '20px',
    padding: '10px 15px',
    fontSize: '13px',
    fontWeight: 'bold',
    color: '#e65100',
    textAlign: 'center',
  },
  evomlClientNode: {
    background: 'linear-gradient(135deg, #ffffff 0%, #f8f8f8 100%)',
    border: '2px solid #333333',
    borderRadius: '12px',
    padding: '12px 16px',
    fontSize: '13px',
    fontWeight: '600',
    color: '#333333',
    width: 160,
    textAlign: 'center',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  },
  agentIntegrationNode: {
    background: 'linear-gradient(135deg, #ffffff 0%, #f8f8f8 100%)',
    border: '2px solid #333333',
    borderRadius: '16px',
    padding: '12px 16px',
    fontSize: '12px',
    fontWeight: '600',
    color: '#333333',
    width: 140,
    textAlign: 'center',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  }
};

const initialNodes = [
  // Core Workflow - Main flow (vertical layout) with automation indicators
  {
    id: 'business-user',
    type: 'automated',
    data: { 
      label: '👤 Business User\n(Problem Owner)\n"I want to build a\ncredit risk model"',
      icon: '👤',
      style: { ...themeStyles.mainStageNode, fontSize: '12px', width: 200 },
      tooltip: 'Business defines the ML problem',
      automated: false
    },
    position: { x: 400, y: 50 },
  },
  {
    id: 'data-scientist',
    type: 'automated',
    data: { 
      label: '👨‍🔬 Data Scientist',
      icon: '👨‍🔬',
      style: themeStyles.mainStageNode,
      tooltip: 'Receives requirements from business user',
      automated: false
    },
    position: { x: 400, y: 200 },
  },
  {
    id: 'advanced-feature-gen',
    type: 'automated',
    data: { 
      label: 'Advanced Feature\nExploration\n📊 Aggregate Dataset',
      icon: '🧬',
      artemisLogo: true,
      style: themeStyles.automatedArtemisNode,
      tooltip: 'Agentic EvoML: Aggregates multiple data sources, explores external data, and generates evolved features',
      automated: true
    },
    position: { x: 400, y: 350 },
  },
  {
    id: 'prepare',
    type: 'automated',
    data: { 
      label: 'Prepare\nData & Features',
      icon: '🔧',
      evomlLogo: true,
      style: themeStyles.automatedEvoMLNode,
      tooltip: 'EvoML: Data cleaning, feature engineering, dimensionality reduction',
      automated: true
    },
    position: { x: 400, y: 500 },
  },
  {
    id: 'build',
    type: 'automated',
    data: { 
      label: 'Build\nML Models',
      icon: '🤖',
      evomlLogo: true,
      style: themeStyles.automatedEvoMLNode,
      tooltip: 'EvoML: Automated model generation in 2 weeks',
      automated: true
    },
    position: { x: 400, y: 650 },
  },
  {
    id: 'evaluate-code',
    type: 'automated',
    data: { 
      label: 'Evaluate & Optimize\nSource Code',
      icon: '📝',
      evomlLogo: true,
      style: themeStyles.automatedEvoMLNode,
      tooltip: 'EvoML & Artemis: Provides clean code and optimizes performance',
      automated: true
    },
    position: { x: 400, y: 800 },
  },
  {
    id: 'model-governance',
    type: 'automated',
    data: { 
      label: 'Governance\nAssistance',
      icon: '🛡️',
      evomlLogo: true,
      style: themeStyles.automatedEvoMLNode,
      tooltip: 'EvoML + Agentic: Enhanced bias detection, explainability, and automated compliance documentation',
      automated: true
    },
    position: { x: 400, y: 950 },
  },
  {
    id: 'deploy',
    type: 'automated',
    data: { 
      label: 'Deploy\nto Production',
      icon: '🚀',
      evomlLogo: true,
      style: themeStyles.automatedEvoMLNode,
      tooltip: 'EvoML: Pre-configured deployment options',
      automated: true
    },
    position: { x: 400, y: 1100 },
  },
  {
    id: 'personalized-apps',
    type: 'automated',
    data: { 
      label: 'Personalized\nApps',
      icon: 'A',
      artemisLogo: true,
      style: themeStyles.automatedArtemisNode,
      tooltip: 'Artemis: Auto-generates custom apps',
      automated: true
    },
    position: { x: 300, y: 1250 },
  },
  {
    id: 'mlops',
    type: 'automated',
    data: { 
      label: 'MLOps\nMonitor & Optimize',
      icon: '📊',
      style: themeStyles.mainStageNode,
      tooltip: 'Future capability: Continuous monitoring and optimization',
      automated: false
    },
    position: { x: 600, y: 1250 },
  },
  {
    id: 'data-sources-db',
    type: 'automated',
    data: { 
      label: 'Database',
      icon: '🗄️',
      style: { ...themeStyles.automatedArtemisNode, width: 100 },
      tooltip: 'SQL/NoSQL databases',
      automated: true
    },
    position: { x: 750, y: 150 },
  },
  {
    id: 'data-sources-api',
    type: 'automated',
    data: { 
      label: 'APIs',
      icon: '🔌',
      style: { ...themeStyles.automatedArtemisNode, width: 100 },
      tooltip: 'REST/GraphQL APIs',
      automated: true
    },
    position: { x: 750, y: 230 },
  },
  {
    id: 'data-sources-files',
    type: 'automated',
    data: { 
      label: 'Files',
      icon: '📁',
      style: { ...themeStyles.automatedArtemisNode, width: 100 },
      tooltip: 'CSV, Excel, JSON files',
      automated: true
    },
    position: { x: 750, y: 310 },
  },
  // Platform indicators
  {
    id: 'evoml-platform',
    type: 'automated',
    data: { 
      label: 'EvoML Platform\nAI-Powered Model Development',
      icon: '🔵',
      evomlLogo: true,
      style: {
        background: 'linear-gradient(135deg, #66bb6a 0%, #81c784 100%)',
        border: 'none',
        borderRadius: '16px',
        padding: '12px 20px',
        fontSize: '13px',
        fontWeight: 'bold',
        color: '#ffffff',
        width: 220,
        textAlign: 'center',
        boxShadow: '0 4px 12px rgba(102, 187, 106, 0.4)',
      }
    },
    position: { x: 50, y: 500 },
  },
  // EvoML Agent Integrations - Unified Model
  {
    id: 'evoml-integrations-header',
    data: { 
      label: '🤖 EvoML Agent Integrations',
      style: {
        background: 'linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)',
        border: 'none',
        borderRadius: '16px',
        padding: '12px 18px',
        fontSize: '13px',
        fontWeight: 'bold',
        color: '#ffffff',
        width: 200,
        textAlign: 'center',
        boxShadow: '0 4px 12px rgba(76, 175, 80, 0.4)',
      }
    },
    position: { x: 30, y: 180 },
  },
  // Python & MCP Integration
  {
    id: 'evoml-python-client',
    type: 'automated',
    data: { 
      label: 'Python Client',
      icon: '🐍',
      evomlLogo: true,
      style: { ...themeStyles.agentIntegrationNode, width: 90 },
      tooltip: 'Direct Python integration',
      automated: true
    },
    position: { x: 30, y: 250 },
  },
  {
    id: 'evoml-mcp-client',
    type: 'automated',
    data: { 
      label: 'MCP Protocol',
      icon: '🔗',
      evomlLogo: true,
      style: { ...themeStyles.agentIntegrationNode, width: 90 },
      tooltip: 'Model Context Protocol',
      automated: true
    },
    position: { x: 140, y: 250 },
  },
  // Agent Grid - 2x2 Layout (positioned to the right of MLOps monitor)
  {
    id: 'vertex-ai-agent',
    type: 'automated',
    data: { 
      label: 'Vertex AI',
      icon: '☁️',
      style: { ...themeStyles.agentIntegrationNode, width: 90 },
      tooltip: 'Google Cloud AI',
      automated: true
    },
    position: { x: 750, y: 1150 },
  },
  {
    id: 'claude-code-agent',
    type: 'automated',
    data: { 
      label: 'Claude Code',
      icon: '💻',
      style: { ...themeStyles.agentIntegrationNode, width: 90 },
      tooltip: 'Local AI assistant',
      automated: true
    },
    position: { x: 860, y: 1150 },
  },
  {
    id: 'artemis-agent',
    type: 'automated',
    data: { 
      label: 'Artemis',
      icon: 'A',
      artemisLogo: true,
      style: { ...themeStyles.agentIntegrationNode, width: 90 },
      tooltip: 'Artemis agent',
      automated: true
    },
    position: { x: 750, y: 1230 },
  },
  {
    id: 'custom-agent',
    type: 'automated',
    data: { 
      label: 'Custom',
      icon: '⚙️',
      style: { ...themeStyles.agentIntegrationNode, width: 90 },
      tooltip: 'Custom agents',
      automated: true
    },
    position: { x: 860, y: 1230 },
  },
];

const initialEdges = [
  // Main workflow pipeline
  {
    id: 'e0',
    source: 'business-user',
    target: 'data-scientist',
    type: 'straight',
    animated: true,
    style: { stroke: '#757575', strokeWidth: 3 },
    markerEnd: { type: MarkerType.ArrowClosed, color: '#757575' }
  },
  {
    id: 'e1',
    source: 'data-scientist',
    target: 'advanced-feature-gen',
    type: 'straight',
    animated: true,
    style: { stroke: '#757575', strokeWidth: 3 },
    markerEnd: { type: MarkerType.ArrowClosed, color: '#757575' }
  },
  {
    id: 'e1a',
    source: 'advanced-feature-gen',
    target: 'prepare',
    type: 'straight',
    animated: true,
    style: { stroke: '#757575', strokeWidth: 3 },
    markerEnd: { type: MarkerType.ArrowClosed, color: '#757575' }
  },
  {
    id: 'e2',
    source: 'prepare',
    target: 'build',
    type: 'straight',
    animated: true,
    style: { stroke: '#757575', strokeWidth: 3 },
    markerEnd: { type: MarkerType.ArrowClosed, color: '#757575' }
  },
  {
    id: 'e3',
    source: 'build',
    target: 'evaluate-code',
    type: 'straight',
    animated: true,
    style: { stroke: '#757575', strokeWidth: 3 },
    markerEnd: { type: MarkerType.ArrowClosed, color: '#757575' }
  },
  {
    id: 'e3a',
    source: 'evaluate-code',
    target: 'model-governance',
    type: 'straight',
    animated: true,
    style: { stroke: '#757575', strokeWidth: 3 },
    markerEnd: { type: MarkerType.ArrowClosed, color: '#757575' }
  },
  {
    id: 'e4',
    source: 'model-governance',
    target: 'deploy',
    type: 'straight',
    animated: true,
    style: { stroke: '#757575', strokeWidth: 3 },
    markerEnd: { type: MarkerType.ArrowClosed, color: '#757575' }
  },
  {
    id: 'e5',
    source: 'deploy',
    target: 'personalized-apps',
    type: 'smoothstep',
    animated: true,
    style: { stroke: '#757575', strokeWidth: 2 },
    markerEnd: { type: MarkerType.ArrowClosed, color: '#757575' }
  },
  {
    id: 'e6',
    source: 'deploy',
    target: 'mlops',
    type: 'smoothstep',
    animated: true,
    style: { stroke: '#757575', strokeWidth: 2 },
    markerEnd: { type: MarkerType.ArrowClosed, color: '#757575' }
  },
  {
    id: 'e8a',
    source: 'data-sources-db',
    target: 'advanced-feature-gen',
    type: 'smoothstep',
    animated: true,
    sourcePosition: 'right',
    targetPosition: 'left',
    style: { stroke: '#757575', strokeWidth: 2 },
    markerEnd: { type: MarkerType.ArrowClosed, color: '#757575' }
  },
  {
    id: 'e8b',
    source: 'data-sources-api',
    target: 'advanced-feature-gen',
    type: 'smoothstep',
    animated: true,
    sourcePosition: 'right',
    targetPosition: 'left',
    style: { stroke: '#757575', strokeWidth: 2 },
    markerEnd: { type: MarkerType.ArrowClosed, color: '#757575' }
  },
  {
    id: 'e8c',
    source: 'data-sources-files',
    target: 'advanced-feature-gen',
    type: 'smoothstep',
    animated: true,
    sourcePosition: 'right',
    targetPosition: 'left',
    style: { stroke: '#757575', strokeWidth: 2 },
    markerEnd: { type: MarkerType.ArrowClosed, color: '#757575' }
  },
  // Feedback loops
  {
    id: 'feedback1',
    source: 'mlops',
    target: 'build',
    type: 'smoothstep',
    style: { stroke: '#757575', strokeWidth: 2, strokeDasharray: '5,5' },
    animated: true,
    sourcePosition: 'left',
    targetPosition: 'right',
    markerEnd: { type: MarkerType.ArrowClosed, color: '#757575' }
  },
];

// Pain points data structure (imported from simplified workflow)
const painPointGroups = [
  {
    stage: 'Problem Definition',
    icon: '👤',
    color: '#8e24aa',
    nodes: ['business-user', 'data-scientist'],
    painPoints: [
      {
        id: 'business-alignment',
        title: 'Business-Technical Alignment Gap',
        description: 'Misalignment between business goals and ML implementation',
        aiAgentSolution: 'AI agents can help translate business objectives into ML problem definitions',
        businessImpact: 'Many ML projects fail due to misaligned objectives',
        automationPotential: '75%'
      }
    ]
  },
  {
    stage: 'Advanced Feature Exploration & Data Aggregation',
    icon: '🧬',
    color: '#9c27b0',
    nodes: ['advanced-feature-gen', 'data-sources-db', 'data-sources-api', 'data-sources-files'],
    painPoints: [
      {
        id: 'data-aggregation',
        title: 'Complex Multi-Source Data Aggregation',
        description: 'Data scientists manually combine data from databases, APIs, and files into a single dataset',
        aiAgentSolution: 'Advanced Feature Exploration automatically aggregates all data sources, handling different formats, schemas, and update frequencies to create a unified dataset',
        businessImpact: 'Weeks spent on manual data integration and reconciliation',
        automationPotential: '95%'
      },
      {
        id: 'external-data-integration',
        title: 'Limited External Data Exploration',
        description: 'Teams miss valuable external data sources that could enhance model performance',
        aiAgentSolution: 'Agentic EvoML with LLMs explores and integrates relevant external data sources automatically, finding correlations across databases',
        businessImpact: 'Models limited to internal data miss crucial predictive signals',
        automationPotential: '80%'
      },
      {
        id: 'evolutionary-features',
        title: 'Static Feature Engineering',
        description: 'Traditional feature engineering is a one-time process with no evolution',
        aiAgentSolution: 'Artemis intelligence continuously evolves features, testing new combinations and transformations based on model performance feedback',
        businessImpact: 'Features become stale and less predictive over time',
        automationPotential: '90%'
      },
      {
        id: 'cross-domain-insights',
        title: 'Siloed Feature Development',
        description: 'Features developed in isolation miss cross-domain patterns and insights',
        aiAgentSolution: 'AI agents identify and transfer successful feature patterns from similar domains and use cases',
        businessImpact: 'Reinventing features that already exist in other domains',
        automationPotential: '85%'
      }
    ]
  },
  {
    stage: 'Data Preparation & Feature Engineering',
    icon: '📊',
    color: '#2196f3',
    nodes: ['prepare'],
    painPoints: [
      {
        id: 'data-cleaning',
        title: 'Manual Data Cleaning',
        description: 'Handling missing values, outliers, and inconsistencies takes weeks',
        aiAgentSolution: 'EvoML automatically cleans data, handles missing values, and detects outliers',
        businessImpact: 'Data scientists waste significant time on repetitive cleaning',
        automationPotential: '90%'
      },
      {
        id: 'feature-engineering',
        title: 'Complex Feature Engineering Process',
        description: 'Feature selection, generation, and dimensionality reduction require deep expertise',
        aiAgentSolution: 'EvoML automates the entire feature engineering pipeline: intelligent feature selection from thousands of columns, automatic generation of feature combinations, and optimal dimensionality reduction',
        businessImpact: 'Manual feature engineering is time-consuming and often misses optimal feature sets',
        automationPotential: '95%'
      },
      {
        id: 'feature-generation',
        title: 'Limited Feature Generation',
        description: 'Data scientists can only explore limited combinations of existing features manually',
        aiAgentSolution: 'EvoML automatically generates and tests thousands of feature combinations, creating polynomial features, interaction terms, and domain-specific transformations',
        businessImpact: 'Critical predictive patterns missed due to manual limitations',
        automationPotential: '95%'
      },
      {
        id: 'dimensionality',
        title: 'Dimensionality Reduction Complexity',
        description: 'Choosing and applying the right dimensionality reduction technique (PCA, LDA, t-SNE, etc.) requires expertise',
        aiAgentSolution: 'EvoML automatically selects and applies optimal dimensionality reduction techniques based on data characteristics and model requirements',
        businessImpact: 'High-dimensional data leads to overfitting and slow training',
        automationPotential: '100%'
      },
      {
        id: 'ml-readiness',
        title: 'Data Not ML-Ready',
        description: 'Raw data requires extensive transformation before ML can be applied',
        aiAgentSolution: 'EvoML transforms any dataset into ML-ready format in minutes',
        businessImpact: 'Months of delay in project start',
        automationPotential: '85%'
      }
    ]
  },
  {
    stage: 'Model Development',
    icon: '🔨',
    color: '#2196f3',
    nodes: ['build'],
    painPoints: [
      {
        id: 'model-selection',
        title: 'Suboptimal Model Selection',
        description: 'Teams often miss the best algorithm for their use case',
        aiAgentSolution: 'AI agents can test all algorithms and ensemble methods automatically',
        businessImpact: 'Significant accuracy improvements missed',
        automationPotential: '95%'
      },
      {
        id: 'hyperparameter-tuning',
        title: 'Manual Hyperparameter Tuning',
        description: 'Finding optimal settings is time-consuming and often skipped',
        aiAgentSolution: 'AI agents use advanced optimization to find best parameters automatically',
        businessImpact: 'Substantial model performance left on table',
        automationPotential: '100%'
      },
      {
        id: 'multi-objective-tradeoffs',
        title: 'Multi-Objective Optimization Complexity',
        description: 'Balancing accuracy vs explainability vs performance is complex',
        aiAgentSolution: 'AI agents can automatically find Pareto-optimal solutions',
        businessImpact: 'Suboptimal models frequently deployed',
        automationPotential: '95%'
      }
    ]
  },
  {
    stage: 'Code Evaluation',
    icon: '🔍',
    color: '#ff6f00',
    nodes: ['evaluate-code'],
    painPoints: [
      {
        id: 'ml-engineering-translation',
        title: 'ML-Engineering Translation Barrier',
        description: 'Data scientists and engineers speak different languages, causing deployment delays',
        aiAgentSolution: 'EvoML generates deploy-ready code that engineers can use directly',
        businessImpact: 'Communication overhead significantly extends deployment timeline',
        automationPotential: '95%'
      },
      {
        id: 'production-code-gap',
        title: 'Data Science Code Not Production-Ready',
        description: 'Research code lacks scalability, error handling, and enterprise standards',
        aiAgentSolution: 'EvoML outputs clean, bug-free, production-grade Python code',
        businessImpact: 'Weeks of additional engineering work per model',
        automationPotential: '100%'
      },
      {
        id: 'code-bugs',
        title: 'Bugs in ML Code',
        description: 'Manual coding introduces bugs that cause production failures',
        aiAgentSolution: 'EvoML generates tested, bug-free code every time',
        businessImpact: 'Production incidents cause major financial losses',
        automationPotential: '100%'
      }
    ]
  },
  {
    stage: 'Governance & Deployment',
    icon: '🛡️',
    color: '#d32f2f',
    nodes: ['model-governance', 'deploy'],
    painPoints: [
      {
        id: 'governance-bottleneck',
        title: 'Manual Model Governance',
        description: 'Model validation and compliance checks slow deployment',
        aiAgentSolution: 'EvoML provides full code, logs, and reports to streamline governance reviews',
        businessImpact: 'Months of additional deployment time',
        automationPotential: '60%'
      },
      {
        id: 'bias-detection',
        title: 'Hidden Model Bias',
        description: 'Models can perpetuate or amplify biases present in training data',
        aiAgentSolution: 'EvoML automatically detects and mitigates bias across protected attributes, providing detailed fairness metrics and bias correction strategies',
        businessImpact: 'Legal risks and reputational damage from biased decisions',
        automationPotential: '85%'
      },
      {
        id: 'model-explainability',
        title: 'Black Box Models',
        description: 'Complex models lack interpretability for regulatory compliance',
        aiAgentSolution: 'EvoML provides comprehensive explainability features including SHAP values, feature importance rankings, and decision path visualization for every prediction',
        businessImpact: 'Cannot deploy in regulated industries without explainability',
        automationPotential: '90%'
      },
      {
        id: 'time-to-market',
        title: 'Slow Time-to-Market',
        description: 'Models take months to deploy, missing business opportunities',
        aiAgentSolution: 'AI agents can automate end-to-end pipeline from idea to production',
        businessImpact: 'Significant revenue loss from delays',
        automationPotential: '85%'
      }
    ]
  },
  {
    stage: 'Production & Apps',
    icon: '📱',
    color: '#4caf50',
    nodes: ['personalized-apps', 'mlops'],
    painPoints: [
      {
        id: 'personalization-complexity',
        title: 'Department-Specific App Development',
        description: 'Each department needs different interfaces and explanations',
        aiAgentSolution: 'AI agents can auto-generate personalized dashboards and explanations',
        businessImpact: 'High cost per custom app development',
        automationPotential: '80%'
      }
    ]
  },
  {
    stage: 'MLOps',
    icon: '🔄',
    color: '#795548',
    nodes: ['mlops'],
    painPoints: [
      {
        id: 'monitoring-drift',
        title: 'Model Performance Decay',
        description: 'Models degrade over time without proper monitoring',
        aiAgentSolution: 'AI agents continuously monitor and retrain models automatically',
        businessImpact: 'Gradual accuracy degradation over time',
        automationPotential: '85%'
      }
    ]
  },
  {
    stage: 'Agent Integrations',
    icon: '🤖',
    color: '#1976d2',
    nodes: ['evoml-python-client', 'evoml-mcp-client', 'vertex-ai-agent', 'claude-code-agent', 'artemis-agent', 'custom-agent'],
    painPoints: [
      {
        id: 'black-box-platforms',
        title: 'Black Box ML Platforms',
        description: 'Most ML platforms are opaque, making it difficult to understand, trust, or interact with models',
        aiAgentSolution: 'EvoML provides complete transparency with full source code, MCP protocol for universal agent access, and Python client for direct integration',
        businessImpact: 'Limited interpretability significantly reduces trust and adoption',
        automationPotential: '100%'
      }
    ]
  }
];

// Automation details sidebar panel
const AutomationSidebar = ({ selectedNode, highlightedStage, highlightedPainPoints, onNodeClick, onClearHighlights, onResetPositions, downloadImage, showExportMenu, setShowExportMenu, sidebarRef, platformFilter, getNodePlatforms }) => {
  const automationGroups = [
    {
      stage: 'Data Preparation',
      icon: '📊',
      color: '#2196f3',
      platform: 'EvoML',
      nodes: ['prepare'],
      automations: ['prepare']
    },
    {
      stage: 'Data Sources',
      icon: '💾',
      color: '#9c27b0',
      platform: 'Agentic',
      nodes: ['data-sources-db', 'data-sources-api', 'data-sources-files'],
      automations: ['data-sources-db', 'data-sources-api', 'data-sources-files']
    },
    {
      stage: 'Model Building',
      icon: '🔨',
      color: '#2196f3',
      platform: 'EvoML',
      nodes: ['build'],
      automations: ['build']
    },
    {
      stage: 'Code Evaluation',
      icon: '🔍',
      color: '#2196f3',
      platform: 'EvoML',
      nodes: ['evaluate-code'],
      automations: ['evaluate-code']
    },
    {
      stage: 'Governance & Deployment',
      icon: '🛡️',
      color: '#2196f3',
      platform: 'EvoML',
      nodes: ['model-governance', 'deploy'],
      automations: ['model-governance', 'deploy']
    },
    {
      stage: 'Production & Monitoring',
      icon: '🎯',
      color: '#2196f3',
      platform: 'EvoML',
      nodes: ['mlops'],
      automations: ['mlops']
    },
    {
      stage: 'Applications',
      icon: '📱',
      color: '#9c27b0',
      platform: 'Agentic',
      nodes: ['personalized-apps'],
      automations: ['personalized-apps']
    },
    {
      stage: 'EvoML Agent Integrations',
      icon: '🤖',
      color: '#1976d2',
      platform: 'EvoML',
      nodes: ['evoml-python-client', 'evoml-mcp-client', 'vertex-ai-agent', 'claude-code-agent', 'artemis-agent', 'custom-agent'],
      automations: ['evoml-python-client', 'evoml-mcp-client', 'vertex-ai-agent', 'claude-code-agent', 'artemis-agent', 'custom-agent']
    }
  ];

  const automationDetails = {
    'prepare': {
      platform: 'EvoML',
      title: 'Automated Data Preparation',
      features: [
        '🧹 Automated data cleaning and preprocessing',
        '🎯 Intelligent feature selection algorithms',
        '📉 Dimensionality reduction (PCA, LDA, etc.)',
        '🔄 Handles missing values and outliers',
        '📊 Makes data ML-ready automatically',
        '⚡ Optimizes data for model performance'
      ],
      benefits: [
        '⏱️ 90% reduction in data prep time',
        '🎯 Optimal feature sets',
        '🚀 From weeks to minutes'
      ],
      videoUrl: 'https://youtu.be/tUX6pjh2pzg',
      videoTitle: 'evoML with Gemini Code Assist'
    },
    'build': {
      platform: 'EvoML',
      title: 'Automated Model Building',
      features: [
        '🤖 Tests 100+ algorithms automatically',
        '🎯 Multi-objective optimization',
        '📊 Generates pure Python code (no dependencies)',
        '💬 Conversational interface for exploration',
        '🔍 Automated bias detection and removal',
        '🎲 Synthetic data generation'
      ],
      benefits: [
        '⏱️ Significantly faster delivery',
        '👥 Junior DS → Senior results',
        '✅ Production-ready code'
      ],
      videoUrl: 'https://youtu.be/XTFyMiUJ_iA',
      videoTitle: 'evoML with Claude Code'
    },
    'evaluate-code': {
      platform: 'EvoML',
      title: 'Code Quality & Readiness',
      features: [
        '✨ Clean, readable Python code with no dependencies',
        '🐛 Bug-free code generation',
        '🏗️ Production-ready architecture',
        '📝 Well-documented functions and classes',
        '🔧 Enterprise-grade error handling',
        '🚀 Deploy-ready from day one'
      ],
      benefits: [
        '🚫 Eliminates translation barrier',
        '⏱️ Zero refactoring needed',
        '✅ Deploy directly to production'
      ],
      videoUrl: 'https://youtu.be/hgn1Jk8LoLE',
      videoTitle: 'evoML with GitHub Copilot'
    },
    'model-governance': {
      platform: 'EvoML',
      title: 'Governance Support & Transparency',
      features: [
        '📝 Provides complete model source code',
        '📊 Full traceability of all model logs',
        '📋 Generates personalized reports',
        '🔍 Complete audit trail of model decisions',
        '💾 All model artifacts downloadable',
        '🔐 Transparent model lineage'
      ],
      benefits: [
        '👁️ Full visibility for governance teams',
        '📑 Audit-ready documentation',
        '🔍 Complete transparency'
      ],
      videoUrl: 'https://youtu.be/RITDizTXbeM',
      videoTitle: 'evoML with Artemis Intelligence'
    },
    'deploy': {
      platform: 'EvoML',
      title: 'Automated Deployment',
      features: [
        '🚀 Pre-configured deployment options',
        '☁️ MLflow, Vertex AI, Ray API support',
        '🔌 REST API generation',
        '🏦 Custom bank integrations (Lloyds)',
        '📦 Zero external dependencies'
      ],
      benefits: [
        '⏱️ Minutes vs weeks',
        '🔧 No engineering translation',
        '✅ Enterprise-ready'
      ],
      videoUrl: 'https://youtu.be/JfLb2O3X39s',
      videoTitle: 'evoML with Cursor'
    },
    'personalized-apps': {
      platform: 'Artemis',
      title: 'Automated App Generation',
      features: [
        '📱 Generates department-specific interfaces',
        '🎨 Custom visualizations per use case',
        '📊 Role-based dashboards',
        '🔐 Built-in access controls'
      ],
      benefits: [
        '💰 Save $50K-200K per app',
        '⏱️ Days instead of months',
        '🎯 Perfect fit for users'
      ],
      videoUrl: 'https://youtu.be/5nEdw_dQQ8s',
      videoTitle: 'Artemis Intelligence Apps'
    },
    'mlops': {
      platform: 'Both',
      title: 'Automated MLOps',
      features: [
        '📈 Continuous model monitoring (EvoML)',
        '🔄 Automatic retraining on drift (EvoML)',
        '⚡ Performance optimization (Artemis)',
        '🧬 Code evolution for improvements (Artemis)',
        '📊 Automated A/B testing (Both)'
      ],
      benefits: [
        '🎯 Maintain peak performance',
        '🔄 Self-improving systems',
        '👥 Minimal human intervention'
      ],
      videoUrl: 'https://youtu.be/PQIBgnT2Whw',
      videoTitle: 'Artemis Intelligence Compliance'
    },
    'data-sources-db': {
      platform: 'Agentic',
      title: 'Database Integration',
      features: [
        '🗄️ Connects to SQL/NoSQL databases',
        '📝 Generates optimal queries',
        '🔄 Handles schema evolution',
        '🚀 Performance optimization'
      ],
      benefits: [
        '⏱️ Instant connectivity',
        '🎯 Query optimization',
        '🔧 Auto-adaptation'
      ],
      videoUrl: 'https://youtu.be/RITDizTXbeM',
      videoTitle: 'Database integration demo'
    },
    'data-sources-api': {
      platform: 'Agentic',
      title: 'API Integration',
      features: [
        '🔌 REST/GraphQL APIs',
        '🔐 Authentication handling',
        '📊 Data transformation',
        '⚡ Rate limit management'
      ],
      benefits: [
        '🌐 Universal API support',
        '🔄 Real-time data',
        '📈 Scalable ingestion'
      ],
      videoUrl: 'https://youtu.be/lCZ_CwzEIQ8',
      videoTitle: 'API integration demo'
    },
    'data-sources-files': {
      platform: 'Agentic',
      title: 'File Integration',
      features: [
        '📁 CSV, Excel, JSON support',
        '🔄 Automatic parsing',
        '📊 Schema inference',
        '💾 Large file handling'
      ],
      benefits: [
        '📂 Any file format',
        '⚡ Fast processing',
        '🎯 Smart parsing'
      ],
      videoUrl: 'https://youtu.be/tUX6pjh2pzg',
      videoTitle: 'File integration demo'
    },
    'evoml-python-client': {
      platform: 'EvoML',
      title: 'EvoML Python Client',
      features: [
        '🐍 Direct Python API integration',
        '📊 Programmatic model building',
        '🔄 Seamless data pipeline integration',
        '📈 Real-time model monitoring'
      ],
      benefits: [
        '⚡ Direct API access',
        '🔧 Programmatic control',
        '📊 Custom integrations'
      ],
      videoUrl: 'https://youtu.be/lCZ_CwzEIQ8',
      videoTitle: 'evoML with Agent Development Kit'
    },
    'evoml-mcp-client': {
      platform: 'EvoML',
      title: 'EvoML MCP Integration',
      features: [
        '🔌 Model Context Protocol support',
        '🤖 Agent-to-agent communication',
        '🌐 Cross-platform compatibility',
        '📡 Real-time model queries'
      ],
      benefits: [
        '🤖 Agent ecosystem integration',
        '🔄 Seamless communication',
        '🌐 Universal compatibility'
      ],
      videoUrl: 'https://youtu.be/JfLb2O3X39s',
      videoTitle: 'evoML with Cursor'
    },
    'vertex-ai-agent': {
      platform: 'Google Cloud',
      title: 'Vertex AI Agent Integration',
      features: [
        '☁️ Google Cloud native integration',
        '🔄 Scalable cloud computing',
        '📊 Enterprise-grade security',
        '🌐 Global deployment capabilities'
      ],
      benefits: [
        '☁️ Cloud-native scaling',
        '🔒 Enterprise security',
        '🌍 Global availability'
      ],
      videoUrl: 'https://youtu.be/dOezAjhsrZg',
      videoTitle: 'evoML with Gemini CLI'
    },
    'claude-code-agent': {
      platform: 'Local Device',
      title: 'Claude Code Agent Integration',
      features: [
        '💻 Local device execution',
        '🔒 Privacy-first approach',
        '⚡ Low-latency interactions',
        '🛠️ Development environment integration'
      ],
      benefits: [
        '🔒 Complete privacy control',
        '⚡ Instant responses',
        '💻 Local development workflow'
      ],
      videoUrl: 'https://youtu.be/XTFyMiUJ_iA',
      videoTitle: 'evoML with Claude Code'
    },
    'artemis-agent': {
      platform: 'Artemis',
      title: 'Artemis Agent Integration',
      features: [
        '🏛️ Native Artemis platform integration',
        '🧬 Code evolution capabilities',
        '📊 Intelligent optimization',
        '🔄 Continuous improvement'
      ],
      benefits: [
        '🧬 Evolutionary optimization',
        '📈 Self-improving code',
        '🎯 Domain-specific intelligence'
      ],
      videoUrl: 'https://youtu.be/RITDizTXbeM',
      videoTitle: 'evoML with Artemis Intelligence'
    },
    'custom-agent': {
      platform: 'Custom',
      title: 'Custom Agent Integration',
      features: [
        '🔧 Build your own agent integrations',
        '🔌 Flexible API connections',
        '🎯 Domain-specific agents',
        '🚀 Enterprise-specific workflows'
      ],
      benefits: [
        '🎨 Complete customization',
        '🔒 Private agent deployments',
        '⚡ Optimized for your use case'
      ],
      videoUrl: 'https://youtu.be/lCZ_CwzEIQ8',
      videoTitle: 'Building custom EvoML agent integrations'
    }
  };

  // Video demos for agent integrations
  const agentIntegrationVideos = [
    {
      id: 1,
      title: 'Chat interface in evoML',
      description: 'evoML can offer advice and insight on trial outcomes to detect bias and overfitting',
      owner: 'Manos, (Paul for recording with a specific use case)',
      url: 'https://youtu.be/XTFyMiUJ_iA'
    },
    {
      id: 2,
      title: 'Agents using evoML',
      description: 'evoML can be used as a tool by data science agents and fit into broader automated workflows, including on GCP',
      owner: 'Paul',
      url: 'https://youtu.be/lCZ_CwzEIQ8'
    },
    {
      id: 3,
      title: 'Artemis running trials on evoML',
      description: 'Artemis can optimise model building through feature discovery and feature generation, thus acting as a data science agent',
      owner: 'Mina',
      url: 'https://youtu.be/RITDizTXbeM'
    },
    {
      id: 4,
      title: 'Artemis customising evoML pipeline apps',
      description: 'Pipelines from evoML can answer specific business questions, and Artemis is a tool that enables this',
      owner: 'Mina',
      url: 'https://youtu.be/5nEdw_dQQ8s'
    },
    {
      id: 5,
      title: 'Artemis performing EU AI act audit on a pipeline',
      description: 'In the future Artemis could automate tasks in model governance and audit',
      owner: 'Mina',
      url: 'https://youtu.be/PQIBgnT2Whw'
    },
    {
      id: 6,
      title: 'evoML pipeline app showing bias analysis, what-if analysis, clustering and anomaly detection',
      description: 'The roadmap of evoML can include many additional features depending on client needs, but these features can already be demonstrated outside of the platform.',
      owner: 'Manal',
      url: 'https://youtu.be/RITDizTXbeM'
    }
  ];

  // const detail = selectedNode ? automationDetails[selectedNode.id] : null;

  const platformColors = {
    'EvoML': '#2196f3',
    'Artemis': '#9c27b0',
    'Both': '#4caf50',
    'Google Cloud': '#4285f4',
    'Local Device': '#ff6b35',
    'Multi-Agent': '#e91e63'
  };

  return (
    <div ref={sidebarRef} style={{
      background: 'white',
      padding: '20px',
      height: '100%',
      overflowY: 'auto',
      boxShadow: '-2px 0 8px rgba(0,0,0,0.1)'
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '15px'
      }}>
        <h3 style={{ 
          color: platformFilter.evoml && platformFilter.agentic ? '#1b5e20' :
                 platformFilter.evoml ? '#388e3c' :
                 platformFilter.agentic ? '#2e7d32' : '#333', 
          margin: 0,
          fontSize: '18px'
        }}>
          🎯 Business Challenges {(platformFilter.evoml || platformFilter.agentic) && 
            `& ${platformFilter.evoml && platformFilter.agentic ? 'EvoML + Agentic' :
                  platformFilter.evoml ? 'EvoML' : 'Agentic EvoML'} Solutions`}
        </h3>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            onClick={onClearHighlights}
            style={{
              padding: '5px 10px',
              background: '#f44336',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '12px'
            }}
          >
            Clear All
          </button>
          <button
            onClick={onResetPositions}
            style={{
              padding: '5px 10px',
              background: '#ff9800',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '12px'
            }}
          >
            🔄 Reset Layout
          </button>
          <div style={{ position: 'relative' }}>
            <button
              onClick={() => setShowExportMenu(!showExportMenu)}
              style={{
                padding: '5px 10px',
                background: '#4caf50',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: 'bold'
              }}
            >
              💾 Export
            </button>
            {showExportMenu && (
              <div style={{
                position: 'absolute',
                top: '100%',
                right: '0',
                background: 'white',
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                padding: '8px',
                zIndex: 101,
                minWidth: '150px',
                marginTop: '4px'
              }}>
                <button
                  onClick={() => downloadImage('png')}
                  style={{
                    display: 'block',
                    width: '100%',
                    padding: '8px 12px',
                    margin: '4px 0',
                    background: 'none',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '13px',
                    textAlign: 'left',
                    color: '#333',
                    transition: 'background 0.2s'
                  }}
                  onMouseEnter={(e) => e.target.style.background = '#f5f5f5'}
                  onMouseLeave={(e) => e.target.style.background = 'none'}
                >
                  📷 PNG (for PowerPoint)
                </button>
                <button
                  onClick={() => downloadImage('pdf')}
                  style={{
                    display: 'block',
                    width: '100%',
                    padding: '8px 12px',
                    margin: '4px 0',
                    background: 'none',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '13px',
                    textAlign: 'left',
                    color: '#333',
                    transition: 'background 0.2s'
                  }}
                  onMouseEnter={(e) => e.target.style.background = '#f5f5f5'}
                  onMouseLeave={(e) => e.target.style.background = 'none'}
                >
                  📄 PDF (document)
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      
      
      <p style={{
        fontSize: '13px',
        color: '#666',
        marginBottom: '15px',
        background: '#f5f5f5',
        padding: '8px',
        borderRadius: '4px'
      }}>
        Click on workflow stages to see pain points and how EvoML solves them
      </p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
        {painPointGroups.map((group) => (
          <div
            key={group.stage}
            data-stage={group.stage}
            style={{
              background: highlightedStage === group.stage ? '#e8f5e9' : '#fafafa',
              border: highlightedStage === group.stage ? '2px solid #4caf50' : '1px solid #ddd',
              borderRadius: '8px',
              padding: '12px',
              transition: 'all 0.3s ease',
              cursor: 'pointer'
            }}
            onClick={() => onNodeClick({ relatedNodes: group.nodes, stage: group.stage })}
          >
            <div style={{
              display: 'flex',
              alignItems: 'center',
              marginBottom: '10px'
            }}>
              <span style={{ fontSize: '16px', marginRight: '8px' }}>{group.icon}</span>
              <h4 style={{ 
                margin: 0, 
                fontSize: '14px',
                color: group.color
              }}>
                {group.stage}
              </h4>
              <div style={{
                marginLeft: 'auto',
                background: group.color,
                color: 'white',
                padding: '2px 8px',
                borderRadius: '10px',
                fontSize: '11px',
                fontWeight: 'bold'
              }}>
                {group.painPoints.length} Pain Point{group.painPoints.length > 1 ? 's' : ''}
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {group.painPoints.map(painPoint => (
                  <div
                    key={painPoint.id}
                    data-pain-point={painPoint.id}
                    onClick={(e) => {
                      e.stopPropagation();
                      onNodeClick({
                        ...painPoint,
                        relatedNodes: group.nodes,
                        stage: group.stage
                      });
                    }}
                    style={{
                      padding: '10px',
                      background: selectedNode?.id === painPoint.id ? '#ffe0b2' :
                                 highlightedPainPoints.includes(painPoint.id) ? '#e3f2fd' : 'white',
                      border: selectedNode?.id === painPoint.id ? '2px solid #ff5722' :
                              highlightedPainPoints.includes(painPoint.id) ? '2px solid #1976d2' : '1px solid #e0e0e0',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      if (selectedNode?.id !== painPoint.id && !highlightedPainPoints.includes(painPoint.id)) {
                        e.currentTarget.style.background = '#f8f9fa';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (selectedNode?.id !== painPoint.id) {
                        e.currentTarget.style.background = highlightedPainPoints.includes(painPoint.id) ? '#e8f5e9' : 'white';
                      }
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ 
                          fontSize: '13px', 
                          fontWeight: 'bold',
                          color: '#333',
                          marginBottom: '5px'
                        }}>
                          {painPoint.title}
                        </div>
                        <p style={{ 
                          margin: '0 0 8px 0', 
                          fontSize: '11px',
                          color: '#666',
                          lineHeight: '1.4'
                        }}>
                          {painPoint.description}
                        </p>
                        
                        <div style={{ marginTop: '8px' }}>
                          <div style={{ 
                            fontSize: '11px', 
                            color: '#d32f2f',
                            marginBottom: '8px'
                          }}>
                            <strong>Impact:</strong> {painPoint.businessImpact}
                          </div>
                          
                          {((platformFilter.evoml && group.nodes.some(nodeId => {
                            const platforms = getNodePlatforms(nodeId);
                            return platforms.includes('evoml');
                          })) || (platformFilter.agentic && group.nodes.some(nodeId => {
                            const platforms = getNodePlatforms(nodeId);
                            return platforms.includes('agentic');
                          }))) && (
                            <div style={{
                              background: platformFilter.evoml && platformFilter.agentic ? 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)' :
                                         platformFilter.evoml ? '#f1f8e9' : 
                                         platformFilter.agentic ? '#e8f5e9' : '#e8f5e9',
                              padding: '8px',
                              borderRadius: '4px',
                              marginBottom: '8px',
                              border: platformFilter.evoml && platformFilter.agentic ? '1px solid #2e7d32' :
                                       platformFilter.evoml ? '1px solid #81c784' :
                                       platformFilter.agentic ? '1px solid #66bb6a' : 'none'
                            }}>
                              <h5 style={{ 
                                margin: '0 0 5px 0', 
                                fontSize: '11px', 
                                color: platformFilter.evoml && platformFilter.agentic ? '#1b5e20' :
                                       platformFilter.evoml ? '#388e3c' :
                                       platformFilter.agentic ? '#2e7d32' : '#2e7d32'
                              }}>
                                🚀 How {platformFilter.evoml && platformFilter.agentic ? 'EvoML & Agentic' : 
                                         platformFilter.evoml ? 'EvoML' : 'Agentic EvoML'} Solves This:
                              </h5>
                              <p style={{ 
                                margin: 0, 
                                fontSize: '11px',
                                color: platformFilter.evoml && platformFilter.agentic ? '#1b5e20' :
                                       platformFilter.evoml ? '#388e3c' :
                                       platformFilter.agentic ? '#2e7d32' : '#2e7d32'
                              }}>
                                {painPoint.aiAgentSolution}
                              </p>
                            </div>
                          )}
                          
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Video demos list for Agent Integrations stage */}
              {group.stage === 'Agent Integrations' && highlightedStage === 'Agent Integrations' && (
                <div style={{
                  marginTop: '15px',
                  padding: '15px',
                  background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                  borderRadius: '8px',
                  border: '1px solid #dee2e6'
                }}>
                  <h4 style={{
                    margin: '0 0 12px 0',
                    fontSize: '14px',
                    color: '#1976d2',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    🎥 Video Demonstrations
                  </h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {agentIntegrationVideos.map(video => (
                      <div key={video.id} style={{
                        background: 'white',
                        padding: '12px',
                        borderRadius: '6px',
                        border: '1px solid #e0e0e0',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-1px)';
                        e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.1)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = 'none';
                      }}>
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          marginBottom: '8px'
                        }}>
                          <h5 style={{
                            margin: 0,
                            fontSize: '12px',
                            fontWeight: 'bold',
                            color: '#333',
                            flex: 1
                          }}>
                            {video.id}. {video.title}
                          </h5>
                          <a 
                            href={video.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            style={{
                              padding: '4px 8px',
                              background: '#1976d2',
                              color: 'white',
                              textDecoration: 'none',
                              borderRadius: '4px',
                              fontSize: '10px',
                              fontWeight: 'bold',
                              marginLeft: '8px'
                            }}
                          >
                            ▶ Watch
                          </a>
                        </div>
                        <p style={{
                          margin: '0 0 6px 0',
                          fontSize: '11px',
                          color: '#666',
                          lineHeight: '1.3'
                        }}>
                          {video.description}
                        </p>
                        <div style={{
                          fontSize: '10px',
                          color: '#888',
                          fontStyle: 'italic'
                        }}>
                          Owner: {video.owner}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Watch Demo button at the end of the section */}
              {((platformFilter.evoml && group.nodes.some(nodeId => {
                const platforms = getNodePlatforms(nodeId);
                return platforms.includes('evoml');
              })) || (platformFilter.agentic && group.nodes.some(nodeId => {
                const platforms = getNodePlatforms(nodeId);
                return platforms.includes('agentic');
              }))) && (
                <div style={{ 
                  marginTop: '12px', 
                  textAlign: 'center',
                  paddingTop: '12px',
                  borderTop: '1px solid #e0e0e0'
                }}>
                  <a 
                    href="https://youtu.be/XTFyMiUJ_iA"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      display: 'inline-block',
                      padding: '8px 16px',
                      background: platformFilter.evoml && platformFilter.agentic ? 
                                 'linear-gradient(135deg, #1565c0 0%, #5e35b1 50%, #8e24aa 100%)' :
                                 platformFilter.evoml ? '#1976d2' :
                                 platformFilter.agentic ? '#8e24aa' : group.color,
                      color: 'white',
                      textDecoration: 'none',
                      borderRadius: '6px',
                      fontSize: '12px',
                      fontWeight: 'bold',
                      transition: 'all 0.2s ease',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.15)'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.transform = 'translateY(-1px)';
                      e.target.style.boxShadow = '0 4px 8px rgba(0,0,0,0.2)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = '0 2px 4px rgba(0,0,0,0.15)';
                    }}
                  >
                    🎥 Watch {platformFilter.evoml && platformFilter.agentic ? 'Combined' :
                             platformFilter.evoml ? 'EvoML' : 'Agentic'} Demo
                  </a>
                </div>
              )}
            </div>
        ))}
      </div>
    </div>
  );
};

// Removed localStorage functionality to ensure all users see the same default positioning

const AutomatedMlWorkflow = ({ title }) => {
  const [nodes, setNodes] = useState(initialNodes);
  const [edges, setEdges] = useState(initialEdges);
  const [selectedNode, setSelectedNode] = useState(null);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [highlightedStage, setHighlightedStage] = useState(null);
  const [showSidebar, setShowSidebar] = useState(true);
  const [highlightedPainPoints, setHighlightedPainPoints] = useState([]); // New state for highlighted pain points
  const sidebarRef = useRef(null); // Ref for sidebar scrolling
  
  // Platform filtering states
  const [platformFilter, setPlatformFilter] = useState({
    evoml: false,
    agentic: false,
    integrations: false // Hidden by default
  });

  const onNodesChange = useCallback((changes) => {
    setNodes((nds) => {
      const updatedNodes = applyNodeChanges(changes, nds);
      
      
      return updatedNodes;
    });
  }, []);

  const onEdgesChange = useCallback((changes) => {
    setEdges((eds) => applyEdgeChanges(changes, eds));
  }, []);

  const handleNodeClick = useCallback((event, node) => {
    if (node && node.data?.automated) {
      setSelectedNode(node);
      setShowSidebar(true);
      
      // Find the corresponding pain point group for this node
      const correspondingGroup = painPointGroups.find(group =>
        group.nodes.includes(node.id)
      );
      
      if (correspondingGroup) {
        setHighlightedStage(correspondingGroup.stage);
      }
    }
  }, []);

  const handleSidebarNodeClick = useCallback((item) => {
    if (item.relatedNodes) {
      // Highlight all nodes in the stage
      setHighlightedStage(item.stage);
      setNodes((nds) => 
        nds.map((node) => ({
          ...node,
          selected: item.relatedNodes.includes(node.id)
        }))
      );
    } else if (item.id) {
      // Highlight specific node
      const node = nodes.find(n => n.id === item.id);
      if (node) {
        setSelectedNode(node);
        setNodes((nds) => 
          nds.map((n) => ({
            ...n,
            selected: n.id === item.id
          }))
        );
      }
    }
  }, [nodes]);

  const handleClearHighlights = useCallback(() => {
    setSelectedNode(null);
    setHighlightedStage(null);
    setHighlightedPainPoints([]);
    setNodes((nds) =>
      nds.map((node) => ({
        ...node,
        selected: false
      }))
    );
  }, []);

  // Reset positions to default
  const handleResetPositions = useCallback(() => {
    localStorage.removeItem('automatedMlWorkflow_nodePositions');
    setNodes(initialNodes);
  }, []);

  // Platform categorization - returns array of platforms a node belongs to
  const getNodePlatforms = useCallback((nodeId) => {
    const evomlNodes = ['build', 'evaluate-code', 'model-governance', 'deploy', 'prepare'];
    const agenticNodes = ['prepare', 'evaluate-code', 'model-governance', 'personalized-apps', 'advanced-feature-gen'];
    const integrationNodes = ['evoml-platform', 'evoml-integrations-header', 'evoml-python-client', 'evoml-mcp-client',
                             'vertex-ai-agent', 'claude-code-agent', 'artemis-agent', 'custom-agent'];
    const coreNodes = ['business-user', 'data-scientist']; // Always visible core workflow nodes
    
    const platforms = [];
    if (evomlNodes.includes(nodeId)) platforms.push('evoml');
    if (agenticNodes.includes(nodeId)) platforms.push('agentic');
    if (integrationNodes.includes(nodeId)) platforms.push('integrations');
    if (coreNodes.includes(nodeId)) platforms.push('core');
    
    return platforms.length > 0 ? platforms : ['other'];
  }, []);

  // Toggle platform filter
  const handlePlatformToggle = useCallback((platform) => {
    setPlatformFilter(prev => {
      const newState = {
        ...prev,
        [platform]: !prev[platform]
      };
      
      // Highlight specific pain points based on platform
      if (platform === 'agentic') {
        if (!prev[platform]) {
          // Turning on agentic filter - highlight feature generation pain point
          setHighlightedPainPoints(['feature-generation']);
        } else {
          // Turning off agentic filter - clear highlighted pain points
          setHighlightedPainPoints([]);
        }
      }
      
      return newState;
    });
  }, []);

  // Get platform-specific styles
  const getPlatformHighlightStyle = useCallback((platforms, originalStyle) => {
    const platformStyles = {
      evoml: {
        background: 'linear-gradient(135deg, #1565c0 0%, #1976d2 100%)',
        border: '3px solid #0d47a1',
        boxShadow: '0 4px 15px rgba(21, 101, 192, 0.4)'
      },
      agentic: {
        background: 'linear-gradient(135deg, #6a1b9a 0%, #8e24aa 100%)',
        border: '3px solid #4a148c',
        boxShadow: '0 4px 15px rgba(106, 27, 154, 0.4)'
      },
      combined: {
        background: 'linear-gradient(135deg, #1565c0 0%, #5e35b1 50%, #8e24aa 100%)',
        border: '3px solid #311b92',
        boxShadow: '0 4px 20px rgba(49, 27, 146, 0.5)'
      }
    };

    // If agentic filter is active AT ALL, use purple/agentic style for nodes that support it
    if (platformFilter.agentic) {
      // Check if this node supports agentic
      const supportsAgentic = Array.isArray(platforms) ? platforms.includes('agentic') : platforms === 'agentic';
      if (supportsAgentic) {
        // Return a completely new object to ensure React detects the change
        return {
          ...originalStyle,
          background: platformStyles.agentic.background,
          border: platformStyles.agentic.border,
          boxShadow: platformStyles.agentic.boxShadow,
          color: '#ffffff',
          fontWeight: 'bold'
        };
      }
    }
    
    // Only fall back to evoml style if agentic is not active
    if (platformFilter.evoml && !platformFilter.agentic) {
      const supportsEvoml = Array.isArray(platforms) ? platforms.includes('evoml') : platforms === 'evoml';
      if (supportsEvoml) {
        // Return a completely new object to ensure React detects the change
        return {
          ...originalStyle,
          background: platformStyles.evoml.background,
          border: platformStyles.evoml.border,
          boxShadow: platformStyles.evoml.boxShadow,
          color: '#ffffff',
          fontWeight: 'bold'
        };
      }
    }

    return { ...originalStyle };
  }, [platformFilter]);

  // Apply platform filtering to nodes
  const filteredNodes = useMemo(() => {
    return nodes.map(node => {
      const platforms = getNodePlatforms(node.id);
      
      // For nodes that can belong to multiple platforms
      const belongsToEvoml = platforms.includes('evoml');
      const belongsToAgentic = platforms.includes('agentic');
      
      if (belongsToEvoml || belongsToAgentic) {
        const activeFilters = [];
        if (platformFilter.evoml && belongsToEvoml) activeFilters.push('evoml');
        if (platformFilter.agentic && belongsToAgentic) activeFilters.push('agentic');
        
        if (activeFilters.length > 0) {
          // Pass array of active filters or single filter
          const platformHighlight = activeFilters.length > 1 ? activeFilters : activeFilters[0];
          
          return {
            ...node,
            data: {
              ...node.data,
              platformHighlight: platformHighlight
            }
          };
        } else {
          return {
            ...node,
            data: {
              ...node.data,
              platformHighlight: null
            }
          };
        }
      }
      
      // For integrations - hide/show based on filter
      if (platforms.includes('integrations')) {
        const isVisible = platformFilter.integrations;
        return {
          ...node,
          hidden: !isVisible
        };
      }
      
      // Core and other nodes always visible with original style
      return node;
    });
  }, [nodes, platformFilter, getNodePlatforms, getPlatformHighlightStyle]);

  // Export functionality
  const downloadImage = async (format) => {
    try {
      setShowExportMenu(false);
      await new Promise(resolve => setTimeout(resolve, 100));
      
      const containerElement = document.querySelector('.react-flow');
      if (!containerElement) {
        alert('Could not find diagram. Please try refreshing the page.');
        return;
      }

      const timestamp = Date.now();
      const filename = `automated-ml-workflow-${timestamp}`;

      if (format === 'png') {
        const canvas = await html2canvas(containerElement, {
          backgroundColor: '#f5f7fa',
          scale: 2,
          logging: false,
          useCORS: true,
          allowTaint: true
        });
        
        canvas.toBlob((blob) => {
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.download = `${filename}.png`;
          link.href = url;
          link.click();
          URL.revokeObjectURL(url);
        });
      } else if (format === 'pdf') {
        const canvas = await html2canvas(containerElement, {
          backgroundColor: '#f5f7fa',
          scale: 2,
          logging: false,
          useCORS: true,
          allowTaint: true
        });
        
        const imgData = canvas.toDataURL('image/png');
        const pdf = new jsPDF({
          orientation: 'landscape',
          unit: 'mm',
          format: 'a4'
        });
        
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = pdf.internal.pageSize.getHeight();
        const imgWidth = canvas.width;
        const imgHeight = canvas.height;
        const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
        const imgX = (pdfWidth - imgWidth * ratio) / 2;
        const imgY = 10;
        
        pdf.addImage(imgData, 'PNG', imgX, imgY, imgWidth * ratio, imgHeight * ratio);
        pdf.save(`${filename}.pdf`);
      }
    } catch (error) {
      console.error('Export failed:', error);
      alert('Export failed. Please try using browser screenshot instead.');
    }
  };
  
  // Auto-scroll to highlighted sections in sidebar
  useEffect(() => {
    if (!sidebarRef.current) return;
    
    // Small delay to ensure DOM is updated
    setTimeout(() => {
      if (highlightedStage) {
        // Scroll to highlighted stage
        const stageElement = sidebarRef.current.querySelector(`[data-stage="${highlightedStage}"]`);
        if (stageElement) {
          stageElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      } else if (highlightedPainPoints.length > 0) {
        // Scroll to first highlighted pain point
        const painPointElement = sidebarRef.current.querySelector(`[data-pain-point="${highlightedPainPoints[0]}"]`);
        if (painPointElement) {
          painPointElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      } else if (selectedNode) {
        // Scroll to selected node's pain point
        const painPointElement = sidebarRef.current.querySelector(`[data-pain-point="${selectedNode.id}"]`);
        if (painPointElement) {
          painPointElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
    }, 100);
  }, [highlightedStage, highlightedPainPoints, selectedNode]);

  return (
    <ReactFlowProvider>
      <div style={{ display: 'flex', height: '100vh', width: '100%', position: 'relative' }}>
        <div style={{ 
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          background: '#f5f7fa'
        }}>

          {/* Interactive Legend */}
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '20px',
            transform: 'translateY(-50%)',
            background: 'white',
            padding: '15px',
            borderRadius: '8px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            zIndex: 100,
            minWidth: '200px'
          }}>
            <h4 style={{ margin: '0 0 10px 0', fontSize: '14px', color: '#333' }}>🎯 Platform Filter</h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', fontSize: '12px' }}>
              <div 
                onClick={() => handlePlatformToggle('evoml')}
                style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '8px',
                  padding: '4px 6px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  background: platformFilter.evoml ? 'transparent' : '#f5f5f5',
                  opacity: platformFilter.evoml ? 1 : 0.6,
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => e.target.style.background = '#e8f5e9'}
                onMouseLeave={(e) => e.target.style.background = platformFilter.evoml ? 'transparent' : '#f5f5f5'}
              >
                <div style={{ 
                  width: '20px', 
                  height: '20px', 
                  background: platformFilter.evoml ? '#c8e6c9' : '#e0e0e0', 
                  border: `2px solid ${platformFilter.evoml ? '#81c784' : '#bdbdbd'}`, 
                  borderRadius: '4px',
                  transition: 'all 0.2s ease'
                }} />
                <span>EvoML</span>
                <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center' }}>
                  {platformFilter.evoml ? <EvoMLLogo size={16} /> : <span style={{ fontSize: '10px' }}>○</span>}
                </span>
              </div>
              
              <div 
                onClick={() => handlePlatformToggle('agentic')}
                style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '8px',
                  padding: '4px 6px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  background: platformFilter.agentic ? 'transparent' : '#f5f5f5',
                  opacity: platformFilter.agentic ? 1 : 0.6,
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => e.target.style.background = '#e8f5e9'}
                onMouseLeave={(e) => e.target.style.background = platformFilter.agentic ? 'transparent' : '#f5f5f5'}
              >
                <div style={{ 
                  width: '20px', 
                  height: '20px', 
                  background: platformFilter.agentic ? '#81c784' : '#e0e0e0', 
                  border: `2px solid ${platformFilter.agentic ? '#4caf50' : '#bdbdbd'}`, 
                  borderRadius: '4px',
                  transition: 'all 0.2s ease'
                }} />
                <span>Agentic EvoML</span>
                <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center' }}>
                  {platformFilter.agentic ? <ArtemisLogo size={16} color="#4caf50" /> : <span style={{ fontSize: '10px' }}>○</span>}
                </span>
              </div>
              
              <div 
                onClick={() => handlePlatformToggle('integrations')}
                style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '8px',
                  padding: '4px 6px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  background: platformFilter.integrations ? 'transparent' : '#f5f5f5',
                  opacity: platformFilter.integrations ? 1 : 0.6,
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => e.target.style.background = '#e8f5e9'}
                onMouseLeave={(e) => e.target.style.background = platformFilter.integrations ? 'transparent' : '#f5f5f5'}
              >
                <div style={{ 
                  width: '20px', 
                  height: '20px', 
                  background: platformFilter.integrations ? '#e8f5e9' : '#e0e0e0', 
                  border: `2px solid ${platformFilter.integrations ? '#66bb6a' : '#bdbdbd'}`, 
                  borderRadius: '4px',
                  transition: 'all 0.2s ease'
                }} />
                <span>EvoML Agent Integrations</span>
                <span style={{ marginLeft: 'auto', fontSize: '10px' }}>{platformFilter.integrations ? '👁️' : '○'}</span>
              </div>
              
              <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #e0e0e0' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '11px', color: '#666' }}>
                  <div style={{ 
                    width: '16px', 
                    height: '16px', 
                    background: '#4caf50', 
                    borderRadius: '50%',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '10px',
                    fontWeight: 'bold'
                  }}>✓</div>
                  <span>Fully Automated</span>
                </div>
              </div>
            </div>
          </div>

          {/* Toggle Sidebar Button */}
          <button
            onClick={() => setShowSidebar(!showSidebar)}
            style={{
              position: 'absolute',
              right: showSidebar ? '520px' : '20px',
              top: '80px',
              padding: '10px',
              background: '#1976d2',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px',
              zIndex: 100,
              transition: 'right 0.3s ease'
            }}
          >
            {showSidebar ? '→' : '←'} {showSidebar ? 'Hide' : 'Show'} Details
          </button>


          <div style={{ 
            flex: 1,
            position: 'relative'
          }}>
          <ReactFlow
            nodes={filteredNodes}
            edges={(() => {
              let filteredEdges = edges.filter(edge => {
                // Hide edges connected to hidden nodes
                const sourceHidden = filteredNodes.find(n => n.id === edge.source)?.hidden;
                const targetHidden = filteredNodes.find(n => n.id === edge.target)?.hidden;
                return !sourceHidden && !targetHidden;
              });
              
              
              return filteredEdges;
            })()}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onNodeClick={handleNodeClick}
            nodeTypes={nodeTypes}
            fitView
            fitViewOptions={{
              padding: 0.2,
              minZoom: 0.5,
              maxZoom: 0.9
            }}
            minZoom={0.3}
            maxZoom={2}
            defaultViewport={{ x: 100, y: 0, zoom: 0.7 }}
            nodesDraggable={true}
            nodesConnectable={false}
            elementsSelectable={true}
            panOnDrag={true}
            panOnScroll={true}
            zoomOnScroll={true}
            zoomOnPinch={true}
          >
            {/* Auto-hiding controls - show on hover */}
            <div 
              style={{
                position: 'absolute',
                top: 10,
                right: 10,
                opacity: 0,
                transition: 'opacity 0.3s ease',
                zIndex: 10,
                pointerEvents: 'none'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.opacity = '1';
                e.currentTarget.style.pointerEvents = 'auto';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.opacity = '0';
                e.currentTarget.style.pointerEvents = 'none';
              }}
            >
              <MiniMap 
                style={{
                  height: 100,
                  width: 150,
                  backgroundColor: 'rgba(0,0,0,0.8)',
                  marginBottom: 10,
                  pointerEvents: 'auto'
                }}
                nodeColor={(node) => node.data?.style?.background || '#666'}
                nodeStrokeColor={() => '#333'}
                nodeBorderRadius={2}
              />
              <Controls style={{ pointerEvents: 'auto' }} />
            </div>
            
            {/* Hover trigger area for controls */}
            <div 
              style={{
                position: 'absolute',
                top: 0,
                right: 0,
                width: 200,
                height: 200,
                zIndex: 5,
                pointerEvents: 'auto'
              }}
              onMouseEnter={(e) => {
                const controlsDiv = e.currentTarget.previousElementSibling;
                if (controlsDiv) {
                  controlsDiv.style.opacity = '1';
                  controlsDiv.style.pointerEvents = 'auto';
                }
              }}
              onMouseLeave={(e) => {
                const controlsDiv = e.currentTarget.previousElementSibling;
                if (controlsDiv) {
                  controlsDiv.style.opacity = '0';
                  controlsDiv.style.pointerEvents = 'none';
                }
              }}
            />
            <Background 
              variant="dots" 
              color="rgba(0,0,0,0.05)" 
              gap={16} 
              size={0.8} 
            />
            </ReactFlow>
          </div>
        </div>

        {/* Sidebar */}
        {showSidebar && (
          <div style={{ 
            width: '500px',
            background: 'white',
            borderLeft: '1px solid #e0e0e0',
            transition: 'width 0.3s ease'
          }}>
            <AutomationSidebar
              selectedNode={selectedNode}
              highlightedStage={highlightedStage}
              highlightedPainPoints={highlightedPainPoints}
              onNodeClick={handleSidebarNodeClick}
              onClearHighlights={handleClearHighlights}
              onResetPositions={handleResetPositions}
              downloadImage={downloadImage}
              showExportMenu={showExportMenu}
              setShowExportMenu={setShowExportMenu}
              sidebarRef={sidebarRef}
              platformFilter={platformFilter}
              getNodePlatforms={getNodePlatforms}
            />
          </div>
        )}
      </div>
    </ReactFlowProvider>
  );
};

export default AutomatedMlWorkflow;